import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

export const createTalkingHeadExplainerVideoSkill: Skill = {
    name: SKILL_NAMES.createTalkingHeadExplainerVideo,
    description:
        'Uploaded recording of someone speaking to camera turned into an explainer — lower third, talking points timed to the speech, and cutaways over a base shot of the speaker. The default recipe for any talking-head or screen-recording upload unless the user asks to keep it plain.',
    kind: 'video',
    surfaces: ['sketch'],
    dashboard: {
        title: 'Talking Head Explainer',
        preview: 'talking-head-explainer',
        frames: [
            'talking-head-explainer-1',
            'talking-head-explainer-2',
            'talking-head-explainer-3',
            'talking-head-explainer-4',
            'talking-head-explainer-5',
        ],
    },
    instructions: `# Create Talking Head Explainer Video
For turning one uploaded recording of a person speaking to camera into an
explainer that stays visually interesting across its whole runtime. The
speaker's own footage is the spine; variety comes from what is layered on top
of it and how the shot is framed — a lower third introducing them, talking
points revealed as they are spoken, occasional cutaways, and moments of
emphasis — always returning to a clean **base scene** of the speaker in
between.

Load \`${SKILL_NAMES.talkingHeadVideoBasics}\` first; it owns the fundamentals this
format is built on (how the recording becomes a scene group, that overlays are
interspersed rather than continuous, and that uncovered stretches of speaker
are correct). This recipe adds the explainer structure on top and does not
repeat any of it.

## HARD RULE: the speaker carries 40-60% of the transcript
**Between 40% and 60% of the transcript stays uncovered** — the speaker on the
base scene with nothing layered over them. That's what makes the overlays read
as deliberate accents rather than a pile-up of effects, and it is the one
proportion this format holds to.

You add every overlay for a scene group in a **single** \`${TOOL_NAMES.addVoiceoverScene}\`
call, so this is something you plan when choosing the word ranges, not something
to notice afterwards. Before making that call, add up the words your ranges
cover against the transcript's total: over 60% covered and the speaker
disappears behind the treatments, so cut or shorten the weakest overlay; under
40% and the video is a plain talking head, so find another moment worth
illustrating.

Where the uncovered stretches fall is a judgement call about the content, not a
rule. Two overlays back to back are fine when the speech genuinely runs two
strong moments together; what matters is that the speaker gets the screen back
often enough across the whole video.

## Setup
Use \`${SKILL_NAMES.gatherRequirements}\` to get necessary info, skipping anything already
provided. Use that skill's general suggestions, but also specifically make sure we get:
- **The speaker's name and title**, for the lower third. Ask once, with
  \`${TOOL_NAMES.askUserQuestion}\`. **If the user doesn't give them, don't add a lower
  third at all** — never invent a name, guess a job title, or leave a
  placeholder on screen.
- **How long the finished video should be.** This decides whether the whole
  recording is used as-is or a section of it is kept.

Two overrides to that skill for this format:
- **Don't ask about voiceover or a script.** The recording is the narration;
  there is nothing to write and nothing to generate.
- **Don't ask the user to supply media for the main subject.** The speaker
  comes from their recording; only cutaway media is sourced separately.

## Trimming to length
- If the whole recording fits the requested length, use it as-is: call
  \`${TOOL_NAMES.addTalkingHeadSceneGroup}\` with just the media_id.
- If it needs to be shorter, **ask which topics matter** before cutting —
  offer the recording's actual sections with \`${TOOL_NAMES.askUserQuestion}\` type
  \`clip_select\`, whose \`clips\` are labelled segments with their exact
  transcript words. Pass the chosen words as \`${TOOL_NAMES.addTalkingHeadSceneGroup}\`'s
  \`words\` so the group covers only that speech.
- **This is the only way to shorten a talking head.** There is no trim tool.
  Never fake one by splitting the group or deleting scenes — say plainly that
  the cut has to happen at insert time, and re-add the group if the user wants
  a different section.

## Captions
- **Check the recording for burned-in captions first** — look at a frame with
  \`${TOOL_NAMES.imageAsk}\`. If the upload already has captions burned into the
  picture, leave captions **off** (\`${TOOL_NAMES.setCaptions}\`); adding ours on top
  double-subtitles the video, which is a defect.
- Otherwise turn captions **on** and **leave them on for the rest of the
  build**. Decide this once, before adding overlays, and don't revisit it — a
  later \`${TOOL_NAMES.setCaptions}\` that turns them off strips them from every
  scene, not just the one you were working on, unless you name that scene in
  \`scene_ids\`. Adding lower thirds, callouts, cutaways, or split-screen text is
  **not** a reason to turn captions off; those sit alongside captions.

## Structure
Build the spine first, then layer treatments onto it in this order.

- **Base crop (first, before any overlay exists).** The raw upload is usually
  framed loosely, and every push-in below is measured against this shot, so
  set it before there is anything to push in from — one \`${TOOL_NAMES.setMedia}\` call
  on the base scene's \`media_1\`. See Framing and crop for how to choose it.
  Skipping this step is the most common way this format comes out flat.
- **Lower third (once, at the start).** Introduce the speaker over their
  opening words with a lower-third design carrying the name and title from
  Setup. Skip it entirely if those weren't provided. **Give it at least 4
  seconds on screen**, longer if the name and title are long.
- **Base scenes (throughout, required).** The speaker with nothing over them.
  These are not gaps to fill — they are the 40-60% above, and where the viewer
  just listens. They all share one base crop (see Framing and crop).
- **Talking points (where the content has them).** When the speaker lists or
  enumerates something, put those points on screen next to them in a
  split-screen or list design, timed so each line appears **as it is spoken**
  (see Asset timing). Use this where the speech genuinely has discrete points
  — don't manufacture a list out of continuous prose.
- **Cutaways (driven by the content).** When the speaker names something
  concrete and visual — a place, a product, an event — overlay media of it for
  a few seconds, then cut back to base. Skip them entirely when nothing in the
  speech is concrete enough to illustrate; a vaguely-related clip is worse than
  staying on the speaker.
- **Emphasis with animated captions (optional).** For one or two high-impact
  lines, an animated-captions design over a punched-in crop of the speaker.
  When you use one, **turn the regular captions off for the video** so the two
  caption treatments never appear together. If the recording has burned-in
  captions, punch the crop in far enough that they sit outside the frame.

## Scene design
- Match each treatment to a design built for it: reach for a lower-third
  design for the intro, a split-screen or list design for talking points, a
  media/b-roll design for cutaways, and an animated-captions design for
  emphasis. Find them with \`${TOOL_NAMES.listSceneDesigns}\` passing
  narrative_thread="talking_head".
- Category labels vary by template and are a hint, not a guarantee — read the
  returned previews rather than trusting a name.
- **Vary the design between consecutive overlays.** Two talking-point scenes
  in a row using the same design reads as a template, not as variety.

## Asset timing — the speech drives everything
- Every overlay covers a range of **transcript words**, so it appears exactly
  when those words are spoken. Add them with \`${TOOL_NAMES.addVoiceoverScene}\`, passing
  every overlay for the group in **one call** rather than one call per scene.
- **Start the range where the content is actually said, not at the start of the
  sentence leading up to it.** Find the words that name the thing the overlay
  shows — the place, the stat, the phrase — and begin there. An overlay that
  appears while the speaker is still working through the clause before it is
  dissonant: the viewer reads something they have not heard yet. Beginning a
  few words early to catch the run-up is fine; beginning a whole sentence
  early is the defect.
- **The lower third is the exception, and it is still required.** It identifies
  the speaker rather than illustrating something they say, and its text comes
  from the questionnaire — so the name usually appears nowhere in the
  transcript and there is no mention to anchor to. Place it over the opening
  words regardless. Never drop it for want of an anchor.
- **Every overlay must stay up long enough to read: 4 seconds minimum, and
  longer for more text.** The word range IS the duration, so a range of a few
  words is a scene that flashes by unread. Check the span using the words'
  start and end times, and when it falls short **extend the range FORWARD into
  what the speaker says next**, never backwards into the run-up — the overlay
  should outlive its mention, not precede it. Running past the end of the
  sentence is fine and normal.
- If a point is named only at the very end of a long sentence, anchor on that
  mention and let the overlay carry into the following sentence.
- **Talking points appear one at a time, as spoken.** Time each line with
  \`${TOOL_NAMES.setText}\` and \`start_words\` (the words that introduce it) as you write it,
  top to bottom, following \`${SKILL_NAMES.voiceoverVideoBasics}\` — a list where every
  point is on screen from the start defeats the purpose of timing it.
- Re-time an overlay that lands wrong with \`${TOOL_NAMES.setSceneRange}\`, or drop it
  with \`${TOOL_NAMES.deleteScene}\`; either just exposes the speaker underneath, which
  is the intended look. Note that the base scene carrying the audio cannot be
  re-timed or deleted — it controls the group's timing.

## Framing and crop
Crop is the main source of visual interest in a format where the footage never
changes. It works in two parts: one baseline you establish and reuse, and a
push-in past it wherever a scene wants emphasis. Crop with \`${TOOL_NAMES.setMedia}\`'s
\`scale\` and \`position\`, and load the \`${SKILL_NAMES.cropMedia}\` skill before the first
one — the position axes are inverted and the translation range depends on scale.

- **Set the base crop once, before adding any overlay.** Raw upload framing is
  usually loose: the speaker sits small, off-centre, or with too much headroom.
  \`${TOOL_NAMES.imageAsk}\` the base scene and reframe until the speaker is properly
  filling the frame and level — typically a modest push-in, not a dramatic one.
  **One \`${TOOL_NAMES.setMedia}\` call is all it takes**: the base crop carries across
  every base scene automatically, so don't re-apply it scene by scene. Doing it
  before the overlays exist also means the push-ins below have a settled
  baseline to be measured against.
- **Push in past the baseline for emphasis.** Every overlay scene should be
  tighter than the base crop, never looser. How much depends on the job:
  - a medium push for a lower third or a callout;
  - a reframe that shifts the speaker to one side for a split-screen, so the
    text half isn't covering them (use \`position\`, not just \`scale\`);
  - the **strongest** push for an animated-captions moment — that's the beat
    the format leans on hardest, and a tight crop is what sells it.
- A push-in works on its own, too: a scene whose only treatment is a tighter
  crop on the speaker is a legitimate emphasis beat and needs no overlay.
- **If the upload has burned-in captions, push in far enough to crop them out**
  on any scene using an animated-captions design, so the two never both show.
- Never crop so tight that the speaker's head is clipped, and keep their face
  clear of wherever the design puts its text.

## Media
- Cutaway media only. Load \`${SKILL_NAMES.findStockMedia}\` before your first stock
  search, and \`${SKILL_NAMES.findDamMedia}\` too when a DAM is connected — for a company
  announcement, real brand footage beats stock.
- Each cutaway illustrates one specific thing the speaker just said. If you
  can't name what it's reinforcing, don't add it.

## Styling
- Colors and styling come from the template or the user's brand kit — apply a
  kit when one exists or the user references their brand (\`${SKILL_NAMES.applyBrandKit}\`
  skill); don't hand-pick color overrides unprompted.

## Finishing
Load \`${SKILL_NAMES.reviewVideo}\` for the standard pass. On top of that, confirm:
- **The speaker carries 40-60% of the transcript.** Add up the words covered by
  overlay scenes in \`${TOOL_NAMES.getVideoState}\` against the transcript total. Over
  60%, shorten or drop the weakest overlay (\`${TOOL_NAMES.setSceneRange}\`,
  \`${TOOL_NAMES.deleteScene}\`); under 40%, add one where the content earns it. This is
  the rule most likely to have slipped, because overlays are placed in one batch.
- **A lower third is present** whenever the user gave a name and title, once,
  near the start, showing exactly what they gave. Its absence is correct ONLY
  when they didn't provide those details — never because no anchor for it
  turned up in the speech.
- **Every overlay is on screen for at least 4 seconds** — read the scene
  durations from \`${TOOL_NAMES.getVideoState}\` and widen the transcript range of any that
  fall short (\`${TOOL_NAMES.setSceneRange}\`), or drop the overlay if there is nothing
  sensible to widen into. Check the lower third specifically, and give an
  overlay carrying several lines more than the 4-second floor. The linter's
  \`voicedriven_scene_reading_time\` only flags text that genuinely can't be read
  in time; the 4-second floor is a higher bar, so the durations are yours to verify.
- **Talking points reveal as they are spoken**, not all at once.
- **Every overlay starts on the words that mention its content.** Read each
  scene's covered words in \`${TOOL_NAMES.getVideoState}\` and check the FIRST few against
  what the overlay shows. If the opening words don't mention it, the overlay is
  mistimed however well it ends — re-anchor it with \`${TOOL_NAMES.setSceneRange}\`,
  moving the start forward to the mention.
- **Captions are handled correctly**: on throughout for a clean recording, off
  entirely if the upload has burned-in captions, and off wherever an
  animated-captions design is used. Never two caption treatments at once.
- **The base crop frames the speaker well** — \`${TOOL_NAMES.imageAsk}\` one base scene and
  confirm they fill the frame and sit level. It applies to every base scene at
  once, so checking one is checking all of them.
- **Every overlay scene is pushed in past that base crop**, with the
  animated-captions scenes the tightest of all. An overlay at or below the base
  scale is a missed emphasis beat, not a neutral choice.
- **The speaker is well framed with their face clear of text in every scene** —
  \`${TOOL_NAMES.imageAsk}\` a sample of scenes to check.
- **Any cutaway obviously matches what was being said** when it appears.
- The video covers the section the user asked for, at roughly the length they
  asked for.`,
};
